import React, { useCallback, useEffect, useState } from 'react';
import { Bell, BellRing, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  CyclingEvent, REGISTRATION_ALERT_ACTIVE_LABEL, REGISTRATION_ALERT_CONFIRMED,
  REGISTRATION_ALERT_LABEL, REGISTRATION_ALERT_TITLE, registrationAlertPromise,
  NEXT_EDITION_ALERT_LABEL, NEXT_EDITION_ALERT_PROMISE,
} from '@radevents/shared';
import { useAuth } from '../contexts/AuthContext';
import { eventService } from '../services/eventService';
import { ORGANIZER_ACCESS_CSS } from './organizer/OrganizerAccessDialog';
import { PrivacyNote } from './PrivacyNote';

/**
 * Die Glocke neben dem Anmeldestand (Trello MSryhIMb). Angemeldet: ein Klick merkt das Event vor,
 * schaltet die Anmeldungs-Nachricht ein und bestätigt. Nicht angemeldet: E-Mail-Adresse, Einmal-Code,
 * fertig. Der Code legt bei Bedarf das Konto an, derselbe Weg wie im Veranstalter-Dialog, dessen
 * Hülle (`oa-*`) hier mitbenutzt wird. Texte: `shared/src/registrationAlert.ts`.
 */

type Step = 'email' | 'code';

/**
 * Die Bestätigung: ein Hinweis unten, der von selbst verschwindet, statt eines eigenen Schritts im
 * Dialog mit drei Absätzen (Phil, 01.10.2026). Dunkel und über der Aktionsleiste, damit er auf dem
 * Handy nicht hinter dem Hauptknopf verschwindet.
 */
export const confirmRegistrationAlert = () => toast.success(REGISTRATION_ALERT_CONFIRMED, {
  // Feste Kennung: ein zweiter Klick ersetzt den Hinweis, statt einen weiteren darüberzustapeln.
  id: 'registration-alert',
  position: 'bottom-center',
  duration: 4000,
  style: {
    background: '#2C2C2C', color: '#fff', border: 'none', fontSize: 15,
    marginBottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
  },
  iconTheme: { primary: '#5FD39A', secondary: '#2C2C2C' },
});

type AlertEvent = Pick<CyclingEvent, 'id' | 'title' | 'registrationStatus' | 'priceTiers' | 'favorited' | 'favoriteCount' | 'participants'>;

export const RegistrationAlertButton: React.FC<{
  event: AlertEvent;
  onEnabled: () => void;
  /** Ausgebucht: „Fürs nächste Jahr benachrichtigen“ statt der Glocke für diese Auflage. */
  nextEdition?: boolean;
  /** `primary`: als Hauptknopf der Handy-Leiste statt als Pille neben dem Anmeldestand. */
  variant?: 'pill' | 'primary';
}> = ({ event, onEnabled, nextEdition, variant = 'pill' }) => {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState<Step | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = !!event.favorited;

  const click = async () => {
    setError(null);
    if (active) return;
    if (!isAuthenticated) { setOpen('email'); return; }
    setBusy(true);
    try {
      await eventService.enableRegistrationAlert(event.id, { via: 'account' });
      onEnabled();
      confirmRegistrationAlert();
    } catch {
      setError('Das hat nicht geklappt. Versuch es bitte noch einmal.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Schon vorgemerkt: nur noch Anzeige, nicht klickbar. Ausgeschaltet wird über das Herz,
          denn Glocke und Merkliste sind derselbe Zustand. */}
      <button type="button" onClick={click} disabled={busy || active} aria-haspopup={active ? undefined : 'dialog'}
        className={variant === 'primary' ? 'ed-organizer-action' : `ra-bell${active ? ' ra-bell--on' : ''}`}
        title={nextEdition ? NEXT_EDITION_ALERT_PROMISE : registrationAlertPromise(event)}>
        {active ? <BellRing size={variant === 'primary' ? 18 : 15} aria-hidden /> : <Bell size={variant === 'primary' ? 18 : 15} aria-hidden />}
        {active ? REGISTRATION_ALERT_ACTIVE_LABEL : nextEdition ? NEXT_EDITION_ALERT_LABEL : REGISTRATION_ALERT_LABEL}
      </button>
      {error && <span className="ra-error" role="alert">{error}</span>}
      {open && (
        <RegistrationAlertDialog event={event} initialStep={open} nextEdition={nextEdition}
          onClose={() => setOpen(null)} onEnabled={onEnabled} />
      )}
    </>
  );
};

export const RegistrationAlertDialog: React.FC<{
  event: AlertEvent;
  initialStep: Step;
  nextEdition?: boolean;
  onClose: () => void;
  onEnabled: () => void;
  /** Nur Design-Lab: feste Adresse und kein Netz. */
  preview?: { email?: string; error?: string };
}> = ({ event, initialStep, nextEdition, onClose, onEnabled, preview }) => {
  const promise = nextEdition ? NEXT_EDITION_ALERT_PROMISE : registrationAlertPromise(event);
  const { requestEmailCode, loginWithEmailCode } = useAuth();
  const [step, setStep] = useState<Step>(initialStep);
  const [email, setEmail] = useState(preview?.email ?? '');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(preview?.error ?? null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(s => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const sendCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (preview) return;
    const address = email.trim();
    if (!address.includes('@') || address.length < 5) {
      setError('Bitte gib deine E-Mail-Adresse an.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await requestEmailCode(address);
      setStep('code');
      setCode('');
      setCooldown(60);
    } catch {
      setError('Der Code konnte nicht gesendet werden. Bitte versuch es noch einmal.');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = useCallback(async (value: string) => {
    if (preview) return;
    setBusy(true);
    setError(null);
    try {
      await loginWithEmailCode(email.trim(), value, { signupSource: 'registration-alert', signupEventId: event.id });
      await eventService.enableRegistrationAlert(event.id, { via: 'dialog' });
      onEnabled();
      onClose();
      confirmRegistrationAlert();
    } catch {
      setError('Der Code stimmt nicht oder ist abgelaufen. Fordere einen neuen an.');
      setCode('');
    } finally {
      setBusy(false);
    }
  }, [preview, loginWithEmailCode, email, event.id, onEnabled, onClose]);

  const onCodeChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    if (digits.length === 6 && !busy) submitCode(digits);
  };

  return (
    <div className="oa-backdrop" role="dialog" aria-modal="true" aria-label={REGISTRATION_ALERT_TITLE}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{ORGANIZER_ACCESS_CSS}</style>
      <div className="oa-dialog">
        <div className="oa-top">
          <span className="oa-step">Anmeldung</span>
          <button type="button" className="oa-icon-btn" onClick={onClose} aria-label="Schließen"><X size={16} /></button>
        </div>

        {step === 'email' && (
          <>
            <h2 className="oa-title">{REGISTRATION_ALERT_TITLE}</h2>
            <p className="oa-lead">{promise}</p>
            <form className="oa-body" onSubmit={sendCode}>
              <div className="oa-picked">
                <span className="ra-dialog-icon" aria-hidden><Bell size={16} /></span>
                <span className="oa-result-name">{event.title}</span>
              </div>
              <label>
                <span className="oa-label">Deine E-Mail-Adresse</span>
                <input className="oa-input" value={email} type="email" autoFocus required autoComplete="email"
                  onChange={e => setEmail(e.target.value)} placeholder="du@beispiel.de" />
              </label>
              <p className="oa-hint">
                Wir schicken dir einen Code, damit wir wissen, dass die Adresse dir gehört.{' '}
                <PrivacyNote withAccount />
              </p>
              {error && <p className="oa-hint" style={{ color: '#8C3B0B' }}>{error}</p>}
              <div className="oa-actions">
                <button type="submit" className="oa-btn oa-btn--primary" disabled={busy}>
                  {busy ? 'Wird gesendet…' : 'Code anfordern'}
                </button>
              </div>
            </form>
          </>
        )}

        {step === 'code' && (
          <>
            <h2 className="oa-title">Code eingeben</h2>
            <p className="oa-lead">
              Wir haben dir einen sechsstelligen Code an <strong>{email.trim()}</strong> geschickt.
              Er gilt zehn Minuten.
            </p>
            <div className="oa-body">
              <input className="oa-code" value={code} inputMode="numeric" autoComplete="one-time-code"
                autoFocus maxLength={6} aria-label="Sechsstelliger Code"
                onChange={e => onCodeChange(e.target.value)} placeholder="······" />
              {error && <p className="oa-hint" style={{ color: '#8C3B0B' }}>{error}</p>}
              <p className="oa-hint">
                Keine Mail bekommen? Sieh im Spam-Ordner nach.{' '}
                {cooldown > 0
                  ? <>Neuer Code in {cooldown} Sekunden.</>
                  : <button type="button" className="oa-link" onClick={() => sendCode()}>Code erneut senden</button>}
              </p>
            </div>
          </>
        )}

      </div>
    </div>
  );
};
